export type ExchangeIdListener = () => void

/**
 * Session cache for `translateExchangeIds`, plus the back-off Graph asks for when it throttles us.
 *
 * The translation from an Office EWS id to an immutable rest id (`AAkAL…`) is what makes a filed message findable
 * again after it moved, and it cannot change: that is the whole point of an immutable id. So it is worth caching
 * for the rest of the session, and until this store existed it was not cached at all — every ItemChanged, and
 * therefore every click on the same message, produced another POST. Five tenants were being throttled on it.
 *
 * Everything is keyed on the mailbox. Graph throttles per mailbox, so a shared mailbox that is being told to back
 * off must not silence the user's own; and an id translated for one mailbox says nothing about another.
 */
export type ExchangeIdStore = {
    /** Bumped on every write, so `useSyncExternalStore` has a stable, primitive snapshot to compare. */
    getRevision(): number
    /** The immutable rest id for this item, when it was translated earlier this session. */
    getImmutableId(mailbox: string | undefined, itemId: string | undefined): string | undefined
    setImmutableId(mailbox: string | undefined, itemId: string | undefined, immutableId: string | undefined): void
    /**
     * Whether Graph is still asking us to back off for this mailbox. Note that this goes false on its own, without
     * a notification: nothing re-renders at the moment the window passes. That is deliberate — the next message the
     * user opens re-evaluates it anyway, and a timer that wakes every tab up is a worse trade.
     */
    isThrottled(mailbox: string | undefined): boolean
    /** Records the Retry-After Graph sent with a 429. */
    throttleUntil(mailbox: string | undefined, until: number): void
    subscribe(listener: ExchangeIdListener): () => void
}

type Entry = {
    ids: Map<string, string>
    throttledUntil?: number
}

export function createExchangeIdStore(): ExchangeIdStore {
    // A plain Map rather than React state: the throttle is written from an axios interceptor, far outside any
    // render, and only the components that subscribe should re-render.
    const entries = new Map<string, Entry>()
    const listeners = new Set<ExchangeIdListener>()
    let revision = 0

    const notify = () => {
        revision += 1
        listeners.forEach(listener => listener())
    }

    const entryFor = (mailbox: string): Entry => {
        const existing = entries.get(mailbox)
        if (existing) {
            return existing
        }
        const created: Entry = { ids: new Map() }
        entries.set(mailbox, created)
        return created
    }

    return {
        getRevision(): number {
            return revision
        },

        getImmutableId(mailbox: string | undefined, itemId: string | undefined): string | undefined {
            return mailbox && itemId ? entries.get(mailbox)?.ids.get(itemId) : undefined
        },

        setImmutableId(mailbox: string | undefined, itemId: string | undefined, immutableId: string | undefined) {
            if (!mailbox || !itemId || !immutableId || entries.get(mailbox)?.ids.get(itemId) === immutableId) {
                return
            }
            entryFor(mailbox).ids.set(itemId, immutableId)
            notify()
        },

        isThrottled(mailbox: string | undefined): boolean {
            const until = mailbox ? entries.get(mailbox)?.throttledUntil : undefined
            return until !== undefined && until > Date.now()
        },

        throttleUntil(mailbox: string | undefined, until: number) {
            if (!mailbox || !Number.isFinite(until)) {
                return
            }
            const entry = entryFor(mailbox)
            if (entry.throttledUntil !== undefined && entry.throttledUntil >= until) {
                return
            }
            entry.throttledUntil = until
            notify()
        },

        subscribe(listener: ExchangeIdListener): () => void {
            listeners.add(listener)
            return () => {
                listeners.delete(listener)
            }
        },
    }
}
